import { Grid, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  IconButton,
  Icons,
  MaterialPalette,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import React, { useState } from 'react';

const useStyles = makeStyles({
  box: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    minHeight: '300px',
    zIndex: 2,
    '& .subtitle': {
      marginTop: 20,
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    margin: 32,
    padding: 0,
  },
  logo: {
    marginBottom: '30px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  diffUserLink: {
    marginBottom: '16px',
  },
  linkContainer: {
    marginTop: '0px',
    '& .sentCodeTextSubtitle': {
      margin: '20px 0px 20px 0px',
    },
  },
  title: {
    textAlign: 'center',
    width: '100%',
  },
  button: {
    width: '190px',
    margin: '0 auto',
    marginTop: '50px',
    justifyContent: 'flex-start',
  },
  linkMargin: {
    marginTop: '20px',
  },
  emailLink: {
    textDecoration: 'none',
  },
  space: {
    marginTop: '20px',
  },
  passwordGrid: {
    marginTop: '10px',
  },
  resetPasswordGrid: {
    marginTop: '20px',
    '& #resetPassword': {
      borderRadius: '4px',
    },
    '& #resetPasswordDisabled': {
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
    },
  },
  resetPasswordLabel: {
    color: MaterialPalette.common.black,
  },
  usernameTextGrid: {
    marginTop: '25px',
  },
  confirmUsernameButtonGrid: {
    marginTop: '36px',
    '& #confirmUsernameButton': {
      borderRadius: '4px',
    },
    '& #confirmUsernameButtonDisabled': {
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
    },
  },
  usernameTextField: {
    textDecorationColor: 'white',
  },
  confirmUsernameLabel: {
    color: MaterialPalette.common.black,
  },
});

interface Props {
  open: boolean;
  onCancel: (newPassword: string) => void;
  onForcedPassword: (newPassword: string) => void;
}

const ForcedPasswordPopup: React.FC<Props> = ({ open, onCancel, onForcedPassword }: Props) => {
  const classes = useStyles();
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState<boolean>(false);
  const [policyMessage, setPolicyMessage] = useState('');

  const handleResetPassword = async () => {
    onForcedPassword(newPassword);
  };

  const handleGetConfirmationViewSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleResetPassword();
  };

  const handleNewPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setNewPassword(value);
    if (
      (value.length < 8 ||
        !RegExp(/.*[a-z].*/).test(value) ||
        !RegExp(/.*[A-Z].*/).test(value) ||
        !RegExp(/.*\d.*/).test(value) ||
        !RegExp(/[$&+,:;=?@#|'<>.^*()%!-]/).test(value)) &&
      value.length > 0
    ) {
      setPolicyMessage(
        'The password should have minimum 8 characters, in that please include one uppercase, one lowercase, one numeric and one special character.',
      );
    } else {
      setPolicyMessage('');
    }
  };

  const handleConfirmNewPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    setPasswordError('');
    const value = event.target.value;
    setConfirmNewPassword(value);
    if (newPassword !== value) {
      setPasswordError('Password did not match');
    }
  };

  const handleClickShowPassword = () => {
    setIsShowPassword(!isShowPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setIsShowConfirmPassword(!isShowConfirmPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClose = () => {
    onCancel('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText="Set Password"
      backButton={false}
      disableEnforceFocus={false}
      disableEscapeKeyDown
      dialogActions={
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item xs={5}>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="primary"
              noMinHeight
              data-testid="logoutcancelbutton"
            >
              Cancel
            </Button>
          </Grid>

          <Grid item xs={5}>
            <Button
              onClick={handleResetPassword}
              disabled={
                newPassword === '' ||
                confirmNewPassword === '' ||
                confirmNewPassword !== newPassword
              }
              variant="contained"
              color="primary"
              noMinHeight
              data-testid="logoutButton"
            >
              New Password
            </Button>
          </Grid>
        </Grid>
      }
      maxWidth="xs"
      noMinHeight
    >
      <form onSubmit={handleGetConfirmationViewSubmit}>
        <Grid container spacing={0} className={classes.linkContainer} justifyContent="center">
          <Typography align="center" className="subtitle sentCodeTextSubtitle">
            Enter below to set your password.
          </Typography>

          <Grid item xs={12}>
            <Grid className={classes.passwordGrid}>
              <TextField
                id="Password"
                key="Password"
                name="Password"
                noMinHeight
                label="New Password"
                onChange={handleNewPassword}
                error={policyMessage !== ''}
                errorText={policyMessage || ' '}
                type={isShowPassword ? 'text' : 'password'}
                value={newPassword}
                size="small"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {isShowPassword ? (
                        <Icons.EyeFeather strokeWidth={2} />
                      ) : (
                        <Icons.EyeOffFeather strokeWidth={2} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
            <Grid className={classes.passwordGrid}>
              <TextField
                id="confirmPassword"
                key="confirmPassword"
                name="confirmPassword"
                noMinHeight
                label="Confirm New Password"
                onChange={handleConfirmNewPassword}
                onFocus={handleConfirmNewPassword}
                error={passwordError !== '' && confirmNewPassword !== ''}
                errorText={passwordError}
                type={isShowConfirmPassword ? 'text' : 'password'}
                value={confirmNewPassword}
                size="small"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownConfirmPassword}
                      edge="end"
                    >
                      {isShowConfirmPassword ? (
                        <Icons.EyeFeather strokeWidth={2} />
                      ) : (
                        <Icons.EyeOffFeather strokeWidth={2} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Dialog>
  );
};

export default ForcedPasswordPopup;
